"use client";

import GameCurrencyIcon from "@casino/ui/Games/GameCurrencyIcon";
import { useGamePageData } from "@casino/ui/Games/hooks";
import {
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  TypographyText,
} from "@workspace/frontend-ui";
import type { FC } from "react";
import CurrencyOptionIcon from "./CurrencyOptionIcon";
import useInGameCurrencySelect from "./useInGameCurrencySelect";

const GameCurrencySelect: FC = () => {
  const {
    isLoggedIn,
    isFunMode,
    isCurrencyChangeEnabled,
    inGameCurrencyList,
    t,
  } = useGamePageData();
  const { isPending, options, selectedCurrency, handleSelect } =
    useInGameCurrencySelect();

  // Fun-mode play spends no real balance, so the control is hidden entirely.
  if (isFunMode) return null;

  const isSelectable =
    isLoggedIn && isCurrencyChangeEnabled && inGameCurrencyList.length > 0;

  if (!isSelectable) return <GameCurrencyIcon />;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        asChild
        disabled={isPending}
        data-testid="in-game-currency-select"
      >
        <button
          type="button"
          aria-label={t(
            "game.controls.currency.change",
            "Change in-game currency",
          )}
          className="rounded-full outline-none focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-50 hover:cursor-pointer transform hover:scale-105 transition-all ease-in-out focus:scale-95"
        >
          <GameCurrencyIcon as="div" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        side="top"
        className="p-1! min-w-[134px]"
      >
        {options.map(({ name, value }) => (
          <DropdownMenuItem
            key={name}
            onSelect={() => handleSelect(value)}
            className={cn(
              "p-2 px-3",
              value === selectedCurrency ? "text-primary" : undefined,
            )}
          >
            <CurrencyOptionIcon currency={value} />
            <TypographyText className="pl-2" as="span">
              {name}
            </TypographyText>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default GameCurrencySelect;
